import { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import debounce from "lodash.debounce";
import "whatwg-fetch";
import {
    Tabs,
    Text,
    ShowMoreButton,
    Container,
    StyledHtml,
    VStack,
    Spinner,
    Input,
} from "@postenbring/hedwig-react";

import Hit from "./Hit.jsx";

export const Search = ({ settings, q, localization, noHitsHtml }) => {
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [visibleCount, setVisibleCount] = useState(settings.startCount || 10);
    const [searchText, setSearchText] = useState(q || "");
    const [counts, setCounts] = useState({});
    const [error, setError] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const firstRenderFinished = useRef(false);
    const [hits, setHits] = useState([]);
    const [offset, setOffset] = useState(0);
    const [categories, setCategories] = useState([]);

    const search = (term, category) => {
        setIsLoading(true);
        const l = window.location;
        const path = `${l.origin}${l.pathname}?q=${encodeURIComponent(term)}${l.hash}`;
        const stateObj = { searchText: term };
        history.replaceState(stateObj, searchText, path);

        handleSearch(term, 0, settings.startCount + settings.loadMoreCount, category);
    };

    const searchDebounced = useCallback(debounce(search, 500), []);

    useLayoutEffect(() => {
        if (q) {
            handleSearch(q, 0, settings.startCount + settings.loadMoreCount, selectedCategory);
            return;
        } else {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (firstRenderFinished.current && searchText.length > 3) {
            searchDebounced(searchText, selectedCategory);
        } else if (!firstRenderFinished.current) {
            firstRenderFinished.current = true;
        }
    }, [searchText]);

    const handleShowMore = () => {
        setVisibleCount(visibleCount + settings.loadMoreCount);
        setOffset(offset + settings.loadMoreCount);
        handleSearch(searchText, offset, settings.loadMoreCount, selectedCategory);
    };

    const handleSearch = (searchTerm, offset, count, category) => {
        const searchUrl = `${settings.searchServiceUrl}?q=${encodeURIComponent(searchTerm)}&type=${category}&start=${offset}&count=${count}`;
        fetch(searchUrl)
            .then((fetchedData) => {
                return fetchedData.json();
            })
            .then((result) => {
                if (result.request.params.q === searchTerm) {
                    setHits(offset > 0 ? [...hits, ...result.hits] : result.hits);
                    setCounts(result.counts);
                    setOffset(offset + count);
                    if (result.categories.includes("content")) {
                        const indexValue = result.categories.indexOf("content");
                        const content = result.categories.splice(indexValue, 1).toString();
                        result.categories.unshift(content);
                    }
                    setCategories(result.categories);
                }
            })
            .catch((error) => {
                setError(error);
            })
            .finally(() => setIsLoading(false));
    };

    const setCategory = (category) => {
        if (category === selectedCategory) {
            return;
        }
        setSelectedCategory(category);
        setVisibleCount(settings.startCount);
        handleSearch(searchText, 0, settings.startCount + settings.loadMoreCount, category);
    };

    const getCategoryLabel = (category) => {
        switch (category) {
            case "content":
                return localization.categoryContentLabel;
            case "unit":
                return localization.unit.categoryLabel;
            case "postalCode":
                return localization.postalCode.categoryLabel;
            case "address":
                return localization.address.categoryLabel;
            case "all":
                return localization.categoryAllLabel;
        }
    };

    const tabItems = () =>
        ["all", ...categories].map((category) => ({
            id: category,
            label: (
                <>
                    {getCategoryLabel(category)}{" "}
                    <Text as="span" variant="body-small">
                        ({counts[category]})
                    </Text>
                </>
            ),
        }));

    const shouldDisplayShowMoreButton =
        hits.length > 0 && counts[selectedCategory] > visibleCount && !isLoading;

    return (
        <>
            <Container>
                <Text className="hds-mb-32" variant="h1">
                    {localization.title}
                </Text>
                <Input
                    defaultValue={q}
                    aria-label={localization.searchFieldPlaceholder}
                    placeholder={localization.searchFieldPlaceholder}
                    type="search"
                    incremental="true"
                    autoComplete="off"
                    onChange={(e) => setSearchText(e.target.value)}
                />
            </Container>

            <Container className="hds-mb-32">
                <Text className="hds-mb-40 hds-mt-64" variant="h2">
                    {localization.searchResultLabel}
                </Text>
                <Tabs defaultTab={selectedCategory}>
                    <Tabs.List>
                        {tabItems().map(({ id, label }) => (
                            <Tabs.Tab key={id} tabId={id} onClick={() => setCategory(id)}>
                                {label}
                            </Tabs.Tab>
                        ))}
                    </Tabs.List>
                    {tabItems().map(({ id }) => (
                        <Tabs.Content key={id} forTabId={id}>
                            <VStack gap="32" className="hds-mt-32">
                                {isLoading ? (
                                    <div className="hds-mt-24 hds-mb-24">
                                        <Spinner delay />
                                    </div>
                                ) : (
                                    <>
                                        {hits.slice(0, visibleCount).map((hit, index) => (
                                            <Hit
                                                hit={hit}
                                                key={index}
                                                hitIndex={index}
                                                localization={localization}
                                            />
                                        ))}
                                        {shouldDisplayShowMoreButton && (
                                            <ShowMoreButton
                                                onClick={handleShowMore}
                                                text={localization.loadMoreLabel}
                                                className="hds-mt-24 hds-mb-24"
                                            />
                                        )}
                                    </>
                                )}
                                {hits.length === 0 && !isLoading && (
                                    <StyledHtml
                                        dangerouslySetInnerHTML={{ __html: noHitsHtml }}
                                    ></StyledHtml>
                                )}
                            </VStack>
                        </Tabs.Content>
                    ))}
                </Tabs>
            </Container>
        </>
    );
};
